// Apolo IDE — S2/S4 · useTaskExecutor: a ponte VIVA do executor task-driven.
//
// Conecta o taskExecutor.runTaskGraph (a peça-mãe) às superfícies de UJI via
// CustomEvents — sem prop-drilling, igual o resto do redesign:
//   • apolo:taskGraph  → o board (useTaskGraph) reflete o DAG ao vivo.
//   • apolo:checkpoint → o Guard panel (CheckpointBar) recebe os checkpoints
//     verdes salvos a cada task green (fecha o S4-E5: UI bridge que faltava).
//
// É OPT-IN e ADITIVO: o dispatch server-side existente continua o default; este
// hook é o modo "rodar o plano como DAG honest-green-gated" no cliente. Os seams
// (runTask/verifyTask/saveCheckpoint/restoreCheckpoint) são INJETADOS pelo
// caller — o hook não sabe rodar agente nem oracle, só orquestra + emite. Assim
// é testável sem LLM/FS (injeta fakes) e o caller pluga o runLocalAgentLoop real.

import { useCallback, useState } from 'react';
import { runTaskGraph } from '../lib/taskExecutor.js';

/** @param {string} [sessionId] */
export default function useTaskExecutor(sessionId) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  /**
   * Roda o grafo de tasks pelo executor, emitindo o contrato de eventos vivo.
   * @param {Array<object>} tasks  tasks com {id, goal?, depends_on?}
   * @param {{
   *   runTask: (task:object)=>Promise<any>,
   *   verifyTask: (task:object, result:any)=>Promise<{green:boolean, evidence?:any}>,
   *   saveCheckpoint?: (task:object)=>Promise<string>|string,
   *   restoreCheckpoint?: (id:string|null)=>Promise<void>|void,
   *   signal?: AbortSignal,
   * }} seams
   */
  const runGraph = useCallback(async (tasks, seams = {}) => {
    const list = Array.isArray(tasks) ? tasks : [];
    setRunning(true);
    setResult(null);

    const emitGraph = (detail) => {
      try { window.dispatchEvent(new CustomEvent('apolo:taskGraph', { detail: { sessionId, ...detail } })); } catch { /* noop */ }
    };

    // Semeia o board com o DAG inicial.
    emitGraph({
      type: 'graph_init',
      tasks: list.map((t) => ({
        id: t.id, goal: t.goal || t.label || t.id,
        depends_on: Array.isArray(t.depends_on) ? t.depends_on : [], status: 'pending',
      })),
    });

    // S4 UI bridge — acumula os checkpoints verdes e dispara apolo:checkpoint
    // pro Guard panel a cada save (mesma forma que a GuardrailsPanel consome).
    const checkpoints = [];
    const wrappedSave = typeof seams.saveCheckpoint === 'function'
      ? async (task) => {
        const id = await seams.saveCheckpoint(task);
        checkpoints.push({ id: id || `cp_${task.id}`, taskId: task.id, createdAt: Date.now() });
        try { window.dispatchEvent(new CustomEvent('apolo:checkpoint', { detail: { sessionId, checkpoints: [...checkpoints] } })); } catch { /* noop */ }
        return id;
      }
      : undefined;

    try {
      const res = await runTaskGraph({
        tasks: list,
        runTask: seams.runTask,
        verifyTask: seams.verifyTask,
        saveCheckpoint: wrappedSave,
        restoreCheckpoint: seams.restoreCheckpoint,
        // #9 — encaminha os eventos por-task pro seam do caller (SessionWorkspace mapeia
        // pra todo_status → TodoDropdown sincroniza AO VIVO), além do emitGraph interno.
        onEvent: (evt) => { emitGraph(evt); try { seams.onEvent?.(evt); } catch { /* sink */ } },
        signal: seams.signal,
        // fix B — waves paralelas atômicas (1/ausente = serial byte-idêntico).
        ...(seams.concurrency ? { concurrency: seams.concurrency } : {}),
      });
      setResult(res);
      return res;
    } finally {
      setRunning(false);
    }
  }, [sessionId]);

  return { running, result, runGraph };
}
